import React, { useState, useEffect, useRef } from 'react';
import { ApiKey } from '../../types/apiKey';
import { getApiKeys, revokeApiKey } from '../../services/apiKeyService';
import { useToast } from '../../hooks/use-toast';
import { CreateApiKeyDialog } from './CreateApiKeyDialog';
import { ApiKeysList } from './ApiKeysList';
import { Copy, Loader2, Key, AlertCircle, Terminal, ChevronDown, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';

const CHAT_ENDPOINT = `${window.location.origin}/api/charlie-chat`;

// ─── Security tooltip ────────────────────────────────────────────────────────

const SecurityTooltip: React.FC = () => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setShow(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-accent"
        aria-label="Security best practices"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
        Best practices
      </button>

      {show && (
        <div className="absolute right-0 top-8 z-50 w-68 rounded-xl border bg-popover shadow-xl p-4 text-sm min-w-[260px]">
          <p className="font-semibold text-popover-foreground mb-2">Security Best Practices</p>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            {[
              'Never commit keys to version control',
              'Use environment variables in your apps',
              'Rotate keys regularly',
              'Set sensible expiration dates',
              'Revoke unused keys immediately',
            ].map((tip) => (
              <li key={tip} className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Snippet builder ─────────────────────────────────────────────────────────

type SnippetLang = 'curl' | 'js' | 'ts' | 'python' | 'php' | 'ruby' | 'go' | 'csharp' | 'java';

const LANGS: { id: SnippetLang; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'js', label: 'JavaScript' },
  { id: 'ts', label: 'TypeScript' },
  { id: 'python', label: 'Python' },
  { id: 'php', label: 'PHP' },
  { id: 'ruby', label: 'Ruby' },
  { id: 'go', label: 'Go' },
  { id: 'csharp', label: 'C#' },
  { id: 'java', label: 'Java' },
];

const buildSnippet = (lang: SnippetLang, url: string): string => {
  switch (lang) {
    case 'curl':
      return `curl -X POST ${url} \\\n  -H "Authorization: Bearer ak_YOUR_KEY_HERE" \\\n  -H "Content-Type: application/json" \\\n  -d '{"messages":[{"role":"user","content":"What are concussion red flags?"}]}'`;
    case 'js':
      return `const res = await fetch('${url}', {\n  method: 'POST',\n  headers: {\n    'Authorization': 'Bearer ak_YOUR_KEY_HERE',\n    'Content-Type': 'application/json',\n  },\n  body: JSON.stringify({\n    messages: [{ role: 'user', content: 'What are concussion red flags?' }],\n  }),\n});\nconsole.log(await res.json());`;
    case 'ts':
      return `const res = await fetch('${url}', {\n  method: 'POST',\n  headers: {\n    Authorization: 'Bearer ak_YOUR_KEY_HERE',\n    'Content-Type': 'application/json',\n  },\n  body: JSON.stringify({\n    messages: [{ role: 'user', content: 'What are concussion red flags?' }],\n  }),\n});\nconst data = await res.json();\nconsole.log(data);`;
    case 'python':
      return `import requests\n\nresponse = requests.post(\n    '${url}',\n    headers={\n        'Authorization': 'Bearer ak_YOUR_KEY_HERE',\n        'Content-Type': 'application/json',\n    },\n    json={'messages': [{'role': 'user', 'content': 'What are concussion red flags?'}]},\n)\nprint(response.json())`;
    case 'php':
      return `<?php\n$ch = curl_init('${url}');\ncurl_setopt_array($ch, [\n    CURLOPT_RETURNTRANSFER => true,\n    CURLOPT_POST           => true,\n    CURLOPT_HTTPHEADER     => [\n        'Authorization: Bearer ak_YOUR_KEY_HERE',\n        'Content-Type: application/json',\n    ],\n    CURLOPT_POSTFIELDS => json_encode([\n        'messages' => [['role' => 'user', 'content' => 'What are concussion red flags?']],\n    ]),\n]);\necho curl_exec($ch);\ncurl_close($ch);`;
    case 'ruby':
      return `require 'net/http'\nrequire 'json'\n\nuri  = URI('${url}')\nhttp = Net::HTTP.new(uri.host, uri.port)\nhttp.use_ssl = uri.scheme == 'https'\n\nreq = Net::HTTP::Post.new(uri.path, {\n  'Authorization' => 'Bearer ak_YOUR_KEY_HERE',\n  'Content-Type'  => 'application/json',\n})\nreq.body = { messages: [{ role: 'user', content: 'What are concussion red flags?' }] }.to_json\nputs JSON.parse(http.request(req).body)`;
    case 'go':
      return `body, _ := json.Marshal(map[string]any{\n    "messages": []map[string]string{\n        {"role": "user", "content": "What are concussion red flags?"},\n    },\n})\nreq, _ := http.NewRequest(http.MethodPost, "${url}", bytes.NewBuffer(body))\nreq.Header.Set("Authorization", "Bearer ak_YOUR_KEY_HERE")\nreq.Header.Set("Content-Type", "application/json")\nresp, _ := http.DefaultClient.Do(req)\ndefer resp.Body.Close()`;
    case 'csharp':
      return `var client = new HttpClient();\nclient.DefaultRequestHeaders.Authorization =\n    new AuthenticationHeaderValue("Bearer", "ak_YOUR_KEY_HERE");\n\nvar payload = new {\n    messages = new[] { new { role = "user", content = "What are concussion red flags?" } }\n};\n\nvar response = await client.PostAsync(\n    "${url}",\n    new StringContent(JsonSerializer.Serialize(payload), Encoding.UTF8, "application/json")\n);\nConsole.WriteLine(await response.Content.ReadAsStringAsync());`;
    case 'java':
      return `var client = HttpClient.newHttpClient();\nvar payload = "{\\\"messages\\\":[{\\\"role\\\":\\\"user\\\",\\\"content\\\":\\\"What are concussion red flags?\\\"}]}";\n\nvar request = HttpRequest.newBuilder()\n    .uri(URI.create("${url}"))\n    .header("Authorization", "Bearer ak_YOUR_KEY_HERE")\n    .header("Content-Type", "application/json")\n    .POST(HttpRequest.BodyPublishers.ofString(payload))\n    .build();\n\nSystem.out.println(client.send(request, BodyHandlers.ofString()).body());`;
  }
};

// ─── Quick Start accordion ────────────────────────────────────────────────────

const QuickStart: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<SnippetLang>('curl');
  const [copied, setCopied] = useState<'url' | 'code' | null>(null);
  const { toast } = useToast();
  const snippet = buildSnippet(lang, CHAT_ENDPOINT);

  const copy = async (text: string, kind: 'url' | 'code') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div className="dashboard-card p-0 overflow-hidden">
      {/* Accordion header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-accent/40 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Terminal className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">Quick Start — Test your key</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t">
          {/* Endpoint row */}
          <div className="flex items-center gap-3 px-6 py-3 bg-muted/40 border-b">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide shrink-0">POST</span>
            <code className="flex-1 font-mono text-xs text-foreground">{CHAT_ENDPOINT}</code>
            <button
              onClick={() => copy(CHAT_ENDPOINT, 'url')}
              className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-accent"
            >
              <Copy className="w-3 h-3" />
              {copied === 'url' ? 'Copied!' : 'Copy'}
            </button>
          </div>

          {/* Language tabs */}
          <div className="flex items-center gap-1 px-6 pt-4 pb-2 overflow-x-auto">
            {LANGS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setLang(id)}
                className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  lang === id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Code block */}
          <div className="relative px-6 pb-6">
            <pre className="rounded-lg border bg-muted/60 p-4 font-mono text-xs leading-relaxed overflow-x-auto whitespace-pre">
              {snippet}
            </pre>
            <button
              onClick={() => copy(snippet, 'code')}
              className="absolute top-2 right-8 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground bg-background border rounded px-2 py-1 transition-colors hover:bg-accent"
            >
              <Copy className="w-3 h-3" />
              {copied === 'code' ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export const ApiKeysSection: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const keys = await getApiKeys();
      setApiKeys(keys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
      toast({ title: 'Error', description: 'Failed to load API keys', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadApiKeys(); }, []);

  const handleRevoke = async (id: string) => {
    try {
      await revokeApiKey(id);
      toast({ title: 'API Key Revoked', description: 'The API key has been revoked successfully' });
      await loadApiKeys();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to revoke API key',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="dashboard-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Key className="w-5 h-5" />
              API Keys
            </h3>
            <p className="text-muted-foreground text-sm mt-1">
              Manage API keys for programmatic access to your workspace. Keep keys secret and never share them publicly.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <SecurityTooltip />
            <CreateApiKeyDialog
              onSuccess={loadApiKeys}
              trigger={
                <Button>
                  <Key className="w-4 h-4 mr-2" />
                  Create API Key
                </Button>
              }
            />
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <QuickStart />

      {/* Keys list */}
      <div className="dashboard-card p-6">
        <h4 className="font-semibold mb-4">Your API Keys</h4>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading API keys...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive opacity-50" />
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={loadApiKeys} variant="outline">Try Again</Button>
          </div>
        ) : (
          <ApiKeysList apiKeys={apiKeys} onRevoke={handleRevoke} loading={loading} />
        )}
      </div>
    </div>
  );
};
